import { useTenantAvailable } from '@api/application-configuration';
import { ShowPasswordInputIcon } from '@unistocks/components/basic/input';
import { Form } from '@unistocks/components/form/form';
import { Page } from '@unistocks/components/page/page';
import { toast } from '@unistocks/components/toast';
import { t } from '@unistocks/localization';
import { useProfileChangePasswordMutation } from '@unistocks/proxy/axios-client/ProfileQuery';
import { usePasswordValidationSchema } from '@unistocks/shared/utils/password-validator';
import { Href, useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';

export const ChangePassword: React.FC = () => {
  const isTenantUser = useTenantAvailable();
  const router = useRouter();
  const { data: passwordSchema } = usePasswordValidationSchema();
  const [showPassword, setShowPassword] = useState(false);

  const schema = useMemo(() => {
    if (!passwordSchema) return passwordSchema;

    const requiredMessage = t('::ThisFieldIsRequired.');

    return passwordSchema.extend({
      currentPassword: z.string(requiredMessage).min(1, requiredMessage)
    });
  }, [passwordSchema]);

  const settingsHref: Href = isTenantUser ? '/(tabs)/(menu)/settings' : '/(admin)/(menu)/settings';

  const { mutate: changePassword, isPending } = useProfileChangePasswordMutation({
    onSuccess: () => {
      toast.success(t('AbpAccount::PasswordChanged'));

      router.back();
    }
  });

  return (
    <Page size={'small'}>
      <Page.ScrollView
        headerTitle={t('AbpAccount::ChangePassword')}
        headerLogo={false}
        breadcrumbs={[
          {
            key: 'settings',
            href: settingsHref,
            name: t('::Menu:Settings'),
            rootPathName: 'settings',
            Icon: () => null
          }
        ]}
      >
        <Page.Content>
          <Form
            schema={schema}
            isLoading={isPending}
            onSubmit={({ currentPassword, password }) =>
              changePassword({ currentPassword, newPassword: password })
            }
          >
            <Form.Input
              name={'currentPassword'}
              label={t('AbpAccount::DisplayName:CurrentPassword')}
              isRequired
              type={showPassword ? 'text' : 'password'}
              textContentType={'password'}
              autoCapitalize={'none'}
              returnKeyType={'next'}
              iconAfter={
                <ShowPasswordInputIcon
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
              }
            />

            <Form.Input
              name={'password'}
              label={t('AbpAccount::DisplayName:NewPassword')}
              isRequired
              type={showPassword ? 'text' : 'password'}
              textContentType={'password'}
              autoCapitalize={'none'}
              returnKeyType={'next'}
              iconAfter={
                <ShowPasswordInputIcon
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
              }
            />

            <Form.Input
              name={'confirmPassword'}
              label={t('AbpAccount::DisplayName:NewPasswordConfirm')}
              isRequired
              type={showPassword ? 'text' : 'password'}
              textContentType={'password'}
              autoCapitalize={'none'}
              returnKeyType={'send'}
              iconAfter={
                <ShowPasswordInputIcon
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
              }
            />
          </Form>
        </Page.Content>
      </Page.ScrollView>
    </Page>
  );
};

ChangePassword.displayName = 'ChangePasswordScreen';
